
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useMemo, useEffect, lazy, Suspense } from 'react';
import { BuildingType, CityStats, AIGoal, NewsItem, StoryEvent, TileData, Milestone, Grid, GameStatus, StatDeltas, RandomEvent, ScenarioObjective, QuizQuestion, ResearchState, Scenario, SimulationBreakdown } from '../types';
import { BUILDINGS } from '../constants';
import { playUiSfx } from '../utils/uiAudio';

import InspectorPanel from './ui/InspectorPanel';
import SOCPanel from './ui/SOCPanel';
import DashboardPanel, { DashboardTab } from './ui/DashboardPanel';
import NewsFeed from './ui/NewsFeed';
import StatsBar from './ui/StatsBar';
import ObjectiveCard from './ui/ObjectiveCard';
import ToolBarEnhanced from './ui/ToolBarEnhanced';
import BuildPreviewPanel from './ui/BuildPreviewPanel';
import { resolveLoadTooltipContent } from '../src/adapters/ui/hooks/useTooltipContent';
import { isGeminiConfigured } from '../services/geminiService';
import type { LayerManager } from '../services/layerManager';

const LayerControlPanel = lazy(() =>
  import('./ui/LayerControlPanel').then((m) => ({ default: m.LayerControlPanel }))
);
const HRDashboardEnhanced = lazy(() => import('./ui/HRDashboardEnhanced'));
const TPRMDashboardEnhanced = lazy(() => import('./ui/TPRMDashboardEnhanced'));
const AuditDashboardEnhanced = lazy(() => import('./ui/AuditDashboardEnhanced'));
const PolicyManagementPanel = lazy(() => import('./ui/PolicyManagementPanel'));
import EventModal from './ui/EventModal';
import GameSettingsDashboard from './ui/GameSettingsDashboard';
import CompactLayerStrip from './ui/CompactLayerStrip';
import SimBreakdownStrip from './ui/SimBreakdownStrip';
import { useHudPreferences } from '../stores/hudPreferences';
import ScenarioBriefingModal from './ui/ScenarioBriefingModal';
import RunSummaryModal from './ui/RunSummaryModal';
import PerformanceOverlay from './ui/PerformanceOverlay';
import type { Policy, PolicyChallenge, PolicyMilestone, PolicyGameEffect } from '../types/policy';

export type ActiveModal = 'none' | 'dashboard' | 'soc' | 'settings' | 'scenario_briefing' | 'building_info' | 'policyManagement';

interface UIOverlayProps {
  stats: CityStats;
  deltas: StatDeltas;
  selectedTool: BuildingType;
  onSelectTool: (type: BuildingType) => void;
  currentGoal: AIGoal | null;
  newsFeed: NewsItem[];
  onClaimReward: () => void;
  isGeneratingGoal: boolean;
  aiEnabled: boolean;
  currentStory?: StoryEvent | null;
  selectedTile: TileData | null;
  onUpgrade: () => void;
  onDelete: () => void;
  onRequestBriefing: () => void;
  onTakeLoan: () => void;
  onRepayLoan: () => void;
  onPitchInvestors: () => void;
  milestones: Milestone[];
  onLaunchProduct: (id: string) => void;
  grid: Grid;
  tickNumber: number;
  gameStatus: GameStatus;
  activeEvent: RandomEvent | null;
  onEventAck: () => void;
  onRestart: () => void;
  onRetryScenario?: () => void;
  onSaveNow?: () => void;
  activeScenario?: Scenario | null; // Changed from ID to full object
  scenarioObjectives: ScenarioObjective[];
  activeQuiz: QuizQuestion | null;
  onQuizAnswer: (answer: string) => void;
  onRequestGoal: () => void;
  onSOCAction: (action: 'contain' | 'ransom' | 'restore') => void;
  research: ResearchState;
  onStartResearch: (id: string) => void;
  
  // State lifted from UIOverlay
  activeModal: ActiveModal;
  onSetActiveModal: (modal: ActiveModal) => void;

  // Building Info Modal (optional)
  selectedBuildingType?: BuildingType | null;
  onSelectBuildingInfo?: (type: BuildingType | null) => void;

  onExtend: () => void;

  // Time Control
  gameSpeed: number;
  setGameSpeed: (speed: number) => void;
  
  // AI Coach
  onAskAICoach: (objective: string) => void;
  
  // Tutorial Help
  onAutoSolve: () => void;
  
  // Analytics
  simBreakdown?: SimulationBreakdown;

  // Performance Monitor
  showPerformanceMonitor: boolean;
  onTogglePerformanceMonitor: () => void;

  // Organizational Management
  orgManagementOpen: string | null;
  onSetOrgManagementOpen: (type: string | null) => void;

  // Enhanced Objectives
  onToggleEnhancedObjectives: () => void;

  // Policy Management
  policies: Policy[];
  policyChallenges: PolicyChallenge[];
  policyMilestones: PolicyMilestone[];
  policyEffects: PolicyGameEffect[];
  activePolicy: Policy | null;
  onCreatePolicy: (templateId: string, customization: Partial<Policy>) => Policy | null;
  onApprovePolicy: (policyId: string) => void;
  onImplementPolicy: (policyId: string) => void;
  onEnforcePolicy: (policyId: string) => void;
  onActivateChallenge: (challengeId: string) => void;
  onPolicyAudit: (policyId?: string) => void;
  onSubmitPolicyForReview?: (policyId: string) => void;
  setActivePolicy: (policy: Policy | null) => void;

  layerManager?: LayerManager;

  /** Hovered grid tile from IsoMap — drives build preview panel */
  hoveredTile?: { x: number; y: number } | null;
}

const UIOverlay: React.FC<UIOverlayProps> = ({
  stats,
  deltas,
  selectedTool,
  onSelectTool,
  currentGoal,
  newsFeed,
  onClaimReward,
  isGeneratingGoal,
  aiEnabled,
  currentStory,
  selectedTile,
  onUpgrade,
  onDelete,
  onTakeLoan,
  onRepayLoan,
  onPitchInvestors,
  milestones,
  onLaunchProduct,
  grid,
  tickNumber,
  gameStatus,
  onRestart,
  onRetryScenario,
  onSaveNow,
  activeScenario,
  scenarioObjectives,
  onRequestGoal,
  onSOCAction,
  research,
  onStartResearch,
  activeEvent,
  onEventAck,
  activeModal,
  onSetActiveModal,
  onExtend,
  gameSpeed,
  setGameSpeed,
  onAskAICoach,
  onAutoSolve,
  simBreakdown,

  // Performance Monitor
  showPerformanceMonitor,
  onTogglePerformanceMonitor,

  // Organizational Management
  orgManagementOpen,
  onSetOrgManagementOpen,

  // Enhanced Objectives
  onToggleEnhancedObjectives,

  // Policy Management
  policies,
  policyChallenges,
  policyMilestones,
  policyEffects,
  activePolicy,
  onCreatePolicy,
  onApprovePolicy,
  onImplementPolicy,
  onEnforcePolicy,
  onActivateChallenge,
  onPolicyAudit,
  onSubmitPolicyForReview,
  setActivePolicy,
  layerManager,
  hoveredTile = null,
}) => {
  const [dashboardStartTab, setDashboardStartTab] = useState<DashboardTab>('checklist');
  const hud = useHudPreferences();
  const hasJumpGate = grid.flat().some(t => t.buildingType === BuildingType.JumpGate && t.isConnected);
  const hudClear = activeModal === 'none' && !orgManagementOpen;

  useEffect(() => {
    void resolveLoadTooltipContent();
  }, []);

  const handleOpenDashboard = (tab: DashboardTab = 'checklist') => {
      setDashboardStartTab(tab);
      onSetActiveModal('dashboard');
  };

  // Logic to determine which buildings are "Recommended" based on current objectives
  const recommendedTypes = useMemo(() => {
    const types = new Set<BuildingType>();
    
    // Check Goal
    if (currentGoal && currentGoal.targetType === 'building_count' && currentGoal.buildingType) {
        types.add(currentGoal.buildingType);
    }

    // Check Scenario Objectives
    scenarioObjectives.forEach(obj => {
        if (obj.completed) return;
        const text = obj.description.toLowerCase();
        
        // Manual mapping of objective keywords to BuildingTypes
        if (text.includes('pipeline')) types.add(BuildingType.Pipeline);
        if (text.includes('road')) types.add(BuildingType.Road);
        if (text.includes('policy hq')) types.add(BuildingType.ComplianceMgmt);
        if (text.includes('audit')) types.add(BuildingType.Monitoring);
        if (text.includes('treasury')) types.add(BuildingType.OpsFunding);
        if (text.includes('support')) types.add(BuildingType.OpsComplaints);
        if (text.includes('aml')) types.add(BuildingType.SecurityAML);
        if (text.includes('fraud')) types.add(BuildingType.SecurityFraud);
        if (text.includes('sanctions')) types.add(BuildingType.Sanctions);
        if (text.includes('incident')) types.add(BuildingType.SecurityIncidents);
        if (text.includes('firewall')) types.add(BuildingType.Firewall);
        if (text.includes('siem')) types.add(BuildingType.SIEM);
        if (text.includes('edr')) types.add(BuildingType.EDR);
        if (text.includes('zero trust')) types.add(BuildingType.ZeroTrust);
        if (text.includes('load balancer')) types.add(BuildingType.LoadBalancer);
        if (text.includes('waf')) types.add(BuildingType.WAF);
        if (text.includes('vault')) types.add(BuildingType.Vault);
        if (text.includes('jump gate')) types.add(BuildingType.JumpGate);
        if (text.includes('diode')) types.add(BuildingType.OneWayDiode);
        if (text.includes('internet')) types.add(BuildingType.Internet);
        if (text.includes('privacy')) types.add(BuildingType.LegalPrivacy);
        if (text.includes('reg e') || text.includes('dispute')) types.add(BuildingType.LegalDisputes);
        if (text.includes('marketing') || text.includes('ad review')) types.add(BuildingType.LegalMarketing);
        if (text.includes('vendor')) types.add(BuildingType.OpsVendors);
    });
    
    return Array.from(types);
  }, [scenarioObjectives, currentGoal]);

  const isMinimalHud = !hud.showAiPanel && !hud.showObjectives && !hud.showNewsFeed && !hud.showLayerPanel;

  const showBuildPreview =
    gameStatus === 'playing' &&
    activeModal === 'none' &&
    !orgManagementOpen &&
    selectedTool !== BuildingType.None &&
    hoveredTile !== null &&
    grid[hoveredTile.y]?.[hoveredTile.x]?.buildingType === BuildingType.None;

  return (
    <div className="absolute inset-0 pointer-events-none flex flex-col justify-between p-2 md:p-4 font-sans z-10 text-slate-100">
      
      {/* Command Menu — fixed top-right, never overlaps stats */}
      <button
        type="button"
        data-testid="game-menu-button"
        onClick={() => { playUiSfx('click'); onSetActiveModal('settings'); }}
        className="pointer-events-auto absolute top-2 right-2 md:top-4 md:right-4 z-50 flex items-center gap-2 px-3 py-2 md:px-4 md:py-2.5 bg-slate-900/95 border border-slate-600 rounded-xl text-slate-200 hover:text-white hover:border-cyan-500/50 shadow-lg backdrop-blur-md transition-colors"
        title="Command Menu — HUD layout, audio, system"
      >
        <span className="text-lg">☰</span>
        <span className="text-[10px] font-bold uppercase tracking-widest hidden sm:inline">Menu</span>
      </button>

      {/* Top HUD — stats + compact layer strip (reserved space for menu) */}
      <div className="pointer-events-auto flex flex-col gap-1.5 w-full max-w-full shrink-0 pr-14 md:pr-24">
        <StatsBar
            stats={stats}
            deltas={deltas}
            showAiOffline={gameStatus === 'playing' && !isGeminiConfigured()}
            onOpenDashboard={() => handleOpenDashboard('checklist')}
            onOpenSOC={() => onSetActiveModal('soc')}
            onOpenFinance={() => handleOpenDashboard('finance')}
            gameSpeed={gameSpeed}
            setGameSpeed={setGameSpeed}
            onOpenHR={() => onSetOrgManagementOpen('hr')}
            onOpenTPRM={() => onSetOrgManagementOpen('tprm')}
            onOpenAudit={() => onSetOrgManagementOpen('audit')}
            onOpenEnhancedObjectives={onToggleEnhancedObjectives}
            showOrgShortcuts={hud.showOrgShortcuts}
            compact={isMinimalHud}
        />
        {gameStatus === 'playing' && hudClear && simBreakdown && (
          <SimBreakdownStrip
            simBreakdown={simBreakdown}
            onOpenFinance={() => handleOpenDashboard('finance')}
            onOpenCompliance={() => handleOpenDashboard('compliance')}
            onOpenSecurity={() => handleOpenDashboard('security')}
          />
        )}
        {hudClear && layerManager && gameStatus === 'playing' && !hud.showLayerPanel && (
          <CompactLayerStrip layerManager={layerManager} />
        )}
      </div>

      {/* AI/Goal Panel — optional via HUD settings */}
      {hud.showAiPanel && hudClear && (
        <div className="pointer-events-auto absolute top-16 right-2 md:right-4 z-30 max-w-[min(20rem,calc(100vw-1rem))]">
            <div className={`w-full bg-slate-900/90 text-white rounded-xl border-2 border-indigo-500/50 shadow-[0_0_20px_rgba(99,102,241,0.2)] backdrop-blur-md overflow-hidden transition-all ${!aiEnabled ? 'opacity-80 grayscale-[0.5]' : ''}`}>
            <div className="bg-indigo-900/80 px-3 md:px-4 py-1.5 md:py-2 flex justify-between items-center border-b border-indigo-700">
                <span className="font-bold uppercase text-[10px] md:text-xs tracking-widest flex items-center gap-2 shadow-sm">
                {aiEnabled ? (
                    <>
                    <span className={`w-2 h-2 rounded-full ${isGeneratingGoal ? 'bg-yellow-400 animate-ping' : 'bg-cyan-400 animate-pulse'}`}></span>
                    Risk Officer AI
                    </>
                ) : (
                    <>
                    <span className="w-2 h-2 rounded-full bg-green-400"></span>
                    Sandbox Mode
                    </>
                )}
                </span>
                {aiEnabled && !currentGoal && !isGeneratingGoal && (
                    <button onClick={() => { playUiSfx('click'); onRequestGoal(); }} className="text-[9px] uppercase font-bold bg-indigo-500 hover:bg-indigo-400 px-2 py-0.5 rounded transition-colors">Request Goal</button>
                )}
            </div>
            
            <div className="p-3 md:p-4 relative">
                {currentStory?.isPlaying && (
                    <div className="absolute top-0 right-0 p-2 opacity-50">
                        <div className="flex gap-0.5 items-end h-4">
                            {[1,2,3,4,5].map(i => <div key={i} className="w-1 bg-cyan-400 animate-pulse" style={{height: `${Math.random()*100}%`, animationDuration: `${0.2 + Math.random()*0.3}s`}}></div>)}
                        </div>
                    </div>
                )}

                {aiEnabled ? (
                currentGoal ? (
                    <>
                    <p className="text-xs md:text-sm font-medium text-indigo-100 mb-2 md:mb-3 leading-tight drop-shadow font-mono">"{currentGoal.description}"</p>
                    
                    <div className="flex justify-between items-center mt-1 md:mt-2 bg-slate-950/60 p-1.5 md:p-2 rounded-lg border border-slate-700/50">
                        <div className="text-[10px] md:text-xs text-slate-300">
                        Target: <span className="font-mono font-bold text-white">
                            {currentGoal.targetType === 'building_count' ? BUILDINGS[currentGoal.buildingType!].name : 
                            currentGoal.targetType === 'budget' ? '$' : 'Comp.'} {currentGoal.targetValue}
                        </span>
                        </div>
                        <div className="text-[10px] md:text-xs text-green-300 font-bold font-mono bg-green-900/20 px-2 py-0.5 rounded border border-green-600/30">
                        +${currentGoal.reward}
                        </div>
                    </div>
    
                    {currentGoal.completed && (
                        <button
                        onClick={onClaimReward}
                        className="mt-2 md:mt-3 w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold py-1.5 md:py-2 px-4 rounded shadow-[0_0_15px_rgba(34,197,94,0.3)] transition-all animate-bounce text-xs md:text-sm uppercase tracking-wide border border-emerald-400/50"
                        >
                        Authorize Budget
                        </button>
                    )}
                    </>
                ) : (
                    <div className="text-xs md:text-sm text-slate-400 py-2 italic flex items-center gap-2">
                    {isGeneratingGoal ? 'Analyzing compliance...' : 'System Idle. Awaiting mandates.'}
                    </div>
                )
                ) : (
                <div className="text-xs md:text-sm text-slate-400 py-1">
                    <p className="mb-1">AI Systems offline.</p>
                </div>
                )}
            </div>
            </div>
        </div>
      )}

      {/* Objectives — optional via HUD settings */}
      {hud.showObjectives && hudClear && (
      <div className="absolute top-20 right-2 md:right-4 pointer-events-auto z-20 max-w-[min(18rem,calc(100vw-1rem))] hidden sm:block">
           <ObjectiveCard 
                milestones={milestones} 
                scenarioObjectives={scenarioObjectives} 
                activeScenario={activeScenario} 
                onAskAICoach={onAskAICoach} 
                onAutoSolve={onAutoSolve}
            />
      </div>
      )}

      {/* Quick Debt Repay — optional via HUD settings */}
      {hud.showQuickDebtButton && stats.loanCount > 0 && hudClear && (
          <div className="absolute bottom-36 md:bottom-52 right-4 pointer-events-auto flex flex-col items-end gap-2 animate-slide-in-right z-40">
              <div className="bg-red-900/80 text-white text-[10px] px-2 py-1 rounded font-bold uppercase tracking-wide animate-pulse border border-red-500/50">
                  Daily Interest: -${stats.loanCount * 25}
              </div>
              <button 
                onClick={() => { playUiSfx(stats.budget >= 1000 ? 'money' : 'error'); onRepayLoan(); }}
                disabled={stats.budget < 1000}
                className={`flex items-center gap-3 px-4 py-2 rounded-l-xl shadow-xl border-y border-l transition-all group
                    ${stats.budget >= 1000 
                        ? 'bg-red-600 hover:bg-red-500 border-red-400 text-white hover:pl-6' 
                        : 'bg-slate-900/80 border-slate-600 text-slate-500 cursor-not-allowed opacity-70'}`}
              >
                  <div className="flex flex-col items-end">
                      <span className="text-[9px] uppercase font-bold tracking-widest group-hover:text-red-100">Quick Repay</span>
                      <span className="font-mono font-bold text-xs">PAY $1,000</span>
                  </div>
                  <div className="text-xl bg-black/20 rounded-full w-8 h-8 flex items-center justify-center">💸</div>
              </button>
          </div>
      )}

      {/* Modals */}
      {activeModal === 'settings' && (
        <GameSettingsDashboard
          onClose={() => onSetActiveModal('none')}
          onExitToMenu={onRestart}
          onSaveNow={onSaveNow}
          showPerformanceMonitor={showPerformanceMonitor}
          onTogglePerformanceMonitor={onTogglePerformanceMonitor}
          onOpenCommandDashboard={() => handleOpenDashboard('checklist')}
          onOpenSOC={() => onSetActiveModal('soc')}
        />
      )}
      {activeModal === 'scenario_briefing' && activeScenario && (
        <ScenarioBriefingModal
          scenario={activeScenario}
          onStart={() => onSetActiveModal('none')}
          onSkip={() => onSetActiveModal('none')}
          onBackToMenu={onRestart}
        />
      )}
      {activeEvent && <EventModal event={activeEvent} onAcknowledge={onEventAck} />}

      {/* Performance Monitor */}
      <PerformanceOverlay visible={showPerformanceMonitor} onClose={onTogglePerformanceMonitor} />

      {hud.showLayerPanel && layerManager && gameStatus === 'playing' && hudClear && (
        <div className="absolute top-28 sm:top-32 bottom-28 md:bottom-36 right-2 sm:left-4 sm:right-auto z-40 pointer-events-auto max-w-[min(20rem,calc(100vw-1rem))] flex flex-col min-h-0">
          <Suspense fallback={null}>
            <LayerControlPanel layerManager={layerManager} />
          </Suspense>
        </div>
      )}

      {/* Organizational Management Modals — ponytail: lazy-loaded enterprise chunk */}
      <Suspense fallback={null}>
        {orgManagementOpen === 'hr' && (
          <HRDashboardEnhanced onClose={() => onSetOrgManagementOpen(null)} budget={stats.budget} />
        )}
        {orgManagementOpen === 'tprm' && (
          <TPRMDashboardEnhanced onClose={() => onSetOrgManagementOpen(null)} budget={stats.budget} />
        )}
        {orgManagementOpen === 'audit' && (
          <AuditDashboardEnhanced onClose={() => onSetOrgManagementOpen(null)} budget={stats.budget} />
        )}
      </Suspense>
      {/* Inspector only shows if no modal is active */}
      {activeModal === 'none' && selectedTile && <InspectorPanel tile={selectedTile} grid={grid} onUpgrade={onUpgrade} onDelete={onDelete} budget={stats.budget} hasJumpGate={hasJumpGate} currentMaxLevel={research.currentMaxLevel} />}
      {activeModal === 'soc' && <SOCPanel stats={stats} onSOCAction={onSOCAction} onClose={() => onSetActiveModal('none')} hasJumpGate={hasJumpGate} />}
      {activeModal === 'dashboard' && <DashboardPanel research={research} onStartResearch={onStartResearch} stats={stats} milestones={milestones} onTakeLoan={onTakeLoan} onRepayLoan={onRepayLoan} onPitchInvestors={onPitchInvestors} onLaunchProduct={onLaunchProduct} onClose={() => onSetActiveModal('none')} onExitToMenu={onRestart} grid={grid} hasJumpGate={hasJumpGate} initialTab={dashboardStartTab} simBreakdown={simBreakdown} orgManagementOpen={orgManagementOpen} onSetOrgManagementOpen={onSetOrgManagementOpen} />}
      <Suspense fallback={null}>
        {activeModal === 'policyManagement' && (
          <PolicyManagementPanel
            policies={policies}
            policyChallenges={policyChallenges}
            policyMilestones={policyMilestones}
            policyEffects={policyEffects}
            activePolicy={activePolicy}
            onClose={() => onSetActiveModal('none')}
            onCreatePolicy={onCreatePolicy}
            onApprovePolicy={onApprovePolicy}
            onImplementPolicy={onImplementPolicy}
            onEnforcePolicy={onEnforcePolicy}
            onActivateChallenge={onActivateChallenge}
            onPolicyAudit={onPolicyAudit}
            onSubmitPolicyForReview={onSubmitPolicyForReview}
            setActivePolicy={setActivePolicy}
          />
        )}
      </Suspense>

      {/* End-of-run summary — sandbox & scenarios (M10) */}
      {(gameStatus === 'won' || gameStatus === 'lost' || gameStatus === 'scenario_won') && (
        <RunSummaryModal
          gameStatus={gameStatus}
          stats={stats}
          tickNumber={tickNumber}
          activeScenario={activeScenario}
          milestones={milestones}
          scenarioObjectives={scenarioObjectives}
          onRestart={onRestart}
          onExtend={gameStatus === 'lost' && !activeScenario ? onExtend : undefined}
          onRetry={activeScenario && onRetryScenario ? onRetryScenario : undefined}
        />
      )}

      {/* Build preview — core gameplay feedback; not gated by minimal HUD */}
      <BuildPreviewPanel
        grid={grid}
        stats={stats}
        selectedTool={selectedTool}
        hoveredTile={hoveredTile}
        visible={showBuildPreview}
      />

      {/* Bottom Bar: Tools & News */}
      <div className="flex flex-col-reverse md:flex-row md:justify-between md:items-end pointer-events-auto mt-auto gap-2 w-full max-w-full">
        <ToolBarEnhanced
            selectedTool={selectedTool}
            onSelectTool={onSelectTool}
            budget={stats.budget}
            unlockedBuildings={research.unlockedBuildings}
            recommendedTypes={recommendedTypes}
        />
        {hud.showNewsFeed && <NewsFeed newsFeed={newsFeed} aiEnabled={aiEnabled} />}
      </div>
      
      {/* Credits — hidden when news feed or inspector consumes bottom-right */}
      {!hud.showNewsFeed && !selectedTile && (
        <div className="absolute bottom-1 right-2 md:right-4 text-[8px] md:text-[9px] text-slate-500 font-mono text-right pointer-events-auto hover:text-white/60 transition-colors">
          <a href="https://x.com/ammaar" target="_blank" rel="noreferrer">Compliance Command</a>
        </div>
      )}
    </div>
  );
};

export default UIOverlay;
